import {useCallback, useContext, useState} from 'react';
import {Modal} from '../../../../../../../components/modal/modal';
import {DialogProps} from '@mui/material';
import Form from './1_form';
import Confirm from './2_confirm';
import Success from './3_success';
import {useTranslation} from 'react-i18next';
import {useForm} from 'react-hook-form';
import {translations} from '../../../../../../../i18n/translations';
import {InvitesRepository} from '../../../../../../../repositories/invites';
import {AffiliationInviteData} from '../../../../../../../backend/interfaces/affiliation-invite-data';
import {InviteSenderType} from '../../../../../../../backend/enums/invite-sender-type';
import {AffiliationType} from '../../../../../../../backend/enums/affiliation-type';
import {InviteType} from '../../../../../../../backend/enums/invite-type';
import {
  AffiliationInviteDataPostInvite,
  PostInvite,
} from '../../../../../../../backend/requests/post-invite';
import {AuthContextType} from '../../../../../../../contexts/auth/types';
import {AuthContext} from '../../../../../../../contexts/auth/context';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';
import {DEFAULT_FORM_VALIDATIONS} from '../../../../../../../utils/constants';
import {Option} from '../../../../../../../components/flat-multi-text-select/flat-multi-text-select';
import {UsersRepository} from '../../../../../../../repositories/users';
import {Product} from '../../../../../../../backend/enums/product';

export type CollaboratorFormInputs = {
  name: string;
  roles: Option[];
  invitationType: InvitationType;
  email?: string;
  phoneNumber?: string;
  countryCode: PhoneNumberExtension;
  quickAccessCode?: string;
};

type PhoneNumberExtension = {
  phone: string;
  code: string;
};

export type InvitationType = 'email' | 'phoneNumber' | 'quickAccessCode';

const AddStaffFlow = ({
  inFlow,
  onClose,
  triggerRefetch,
}: {
  inFlow: boolean;
  onClose: () => void;
  triggerRefetch: () => void;
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const authContext: AuthContextType = useContext(AuthContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  const sender = {
    id: authContext.state.session?.selectedAffiliation?.entity.id || '',
    type: InviteSenderType.Entity,
  };

  const entity = {
    id: authContext.state.session?.selectedAffiliation?.entity.id || '',
    name: authContext.state.session?.selectedAffiliation?.entity.name || '',
  };

  const institution = authContext.state.session?.selectedAffiliation?.entity.name || '';

  const {t} = useTranslation();

  const formInstance = useForm<CollaboratorFormInputs>({
    defaultValues: {
      roles: [],
      invitationType: 'email',
      phoneNumber: '',
      countryCode: {code: 'PT', phone: '+351'},
      quickAccessCode: '',
    },
    ...DEFAULT_FORM_VALIDATIONS,
  });

  const nextStep = () => {
    if (currentStep === DIALOGS.length - 1) closeFlow();
    if (currentStep < DIALOGS.length - 1) setCurrentStep((state) => state + 1);
  };

  const closeFlow = useCallback(() => {
    setCurrentStep(0);
    onClose();
    formInstance.reset();
  }, [formInstance, onClose]);

  const submitInvite = async () => {
    const {name, email, roles, invitationType, countryCode, phoneNumber, quickAccessCode} =
      formInstance.getValues();

    if (isSubmitting) return;

    try {
      // Check if the entity is set
      if (!entity) throw new Error('No entity set.');

      setIsSubmitting(true);

      // Specific data
      let successMessage = '';

      // Switch between invitation types
      switch (invitationType) {
        case 'email':
        case 'phoneNumber':
          successMessage =
            translations.professional_caregivers.root.modals.add_staff_flow.snackbar
              .success_message;
          break;
        case 'quickAccessCode':
          successMessage =
            translations.professional_caregivers.root.modals.add_staff_flow.snackbar
              .success_message_create_profile;
          break;
      }

      // Check type
      if (invitationType === 'quickAccessCode') {
        // Create Profile
        await UsersRepository.instance.createCaregiver(entity.id, {
          // Required
          product: authContext.state.session?.currentUser.product ?? Product.Bluebird,
          name: name,
          isActive: true,
          // Non-required
          rolesIds: roles.map((role) => role.key),
          quickAccessCode: quickAccessCode,
        });
      } else {
        // Request body
        const requestBody: PostInvite<AffiliationInviteDataPostInvite> = {
          type: InviteType.Affiliation,
          senderType: sender.type,
          senderId: sender.id,
          data: {
            targetEntityId: entity.id,
            type: AffiliationType.Caregiver,
            rolesIds: roles.map((role) => role.key),
            destinationName: name?.trim(),
            ...(invitationType === 'email'
              ? {destinationEmail: email}
              : {destinationPhoneNumber: `${countryCode.phone}${phoneNumber}`}),
          },
        };

        // Send invite
        await InvitesRepository.instance.createInvite<AffiliationInviteData>(requestBody);
      }

      // Show toast
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(successMessage, {
          name,
        }),
      });

      nextStep();
      triggerRefetch();
    } catch (error) {
      // Log
      console.debug('🔴 Could not send invite.', error);

      // Get toast message by type
      let errorMessage = '';
      switch (invitationType) {
        case 'email':
        case 'phoneNumber':
          errorMessage =
            translations.professional_caregivers.root.modals.add_staff_flow.snackbar.error_message;
          break;
        case 'quickAccessCode':
          errorMessage =
            translations.professional_caregivers.root.modals.add_staff_flow.snackbar
              .error_message_create_profile;
          break;
      }

      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(errorMessage, {
          name,
        }),
      });

      closeFlow();
    }

    setIsSubmitting(false);
  };

  const defaultDialogProps = {
    onClose: closeFlow,
    fullWidth: true,
    maxWidth: 'sm',
    open: inFlow,
    preventCloseClick: true,
  } as DialogProps;

  // Get titles
  let formSubmitButtonTitle =
    translations.professional_caregivers.root.modals.add_staff_flow.form.send_invite;
  let confirmSubmitButtonTitle =
    translations.professional_caregivers.root.modals.add_staff_flow.confirm.send_invite;
  let confirmTitle = translations.professional_caregivers.root.modals.add_staff_flow.confirm.title;
  let successTitle = translations.professional_caregivers.root.modals.add_staff_flow.success.title;

  // Check type
  if (formInstance.getValues('invitationType') === 'quickAccessCode') {
    formSubmitButtonTitle =
      translations.professional_caregivers.root.modals.add_staff_flow.form.create_profile;
    confirmSubmitButtonTitle =
      translations.professional_caregivers.root.modals.add_staff_flow.confirm.create_profile;
    confirmTitle =
      translations.professional_caregivers.root.modals.add_staff_flow.confirm.title_quickAccessCode;
    successTitle =
      translations.professional_caregivers.root.modals.add_staff_flow.success.title_quickAccessCode;
  }

  const DIALOGS = [
    () => (
      <Modal
        id="add-staff-form"
        title={t(translations.professional_caregivers.root.modals.add_staff_flow.form.title)}
        actions={[
          {
            label: t(translations.professional_caregivers.root.modals.add_staff_flow.form.cancel),
            type: 'destructive',
            onClick: closeFlow,
          },
          {
            label: t(formSubmitButtonTitle),
            type: 'nondestructive',
            onClick: nextStep,
            disabled: !formInstance.formState.isValid,
          },
        ]}
        {...defaultDialogProps}
      >
        <Form entityId={entity.id} formInstance={formInstance} />
      </Modal>
    ),
    () => (
      <Modal
        id="add-staff-confirm"
        title={t(confirmTitle)}
        actions={[
          {
            label: t(
              translations.professional_caregivers.root.modals.add_staff_flow.confirm.cancel
            ),
            type: 'destructive',
            onClick: closeFlow,
            disabled: isSubmitting,
          },
          {
            label: t(confirmSubmitButtonTitle),
            type: 'nondestructive',
            onClick: submitInvite,
            loading: isSubmitting,
          },
        ]}
        {...defaultDialogProps}
      >
        <Confirm
          {...{
            name: formInstance.getValues('name'),
            invitationType: formInstance.getValues('invitationType'),
            institution,
          }}
        />
      </Modal>
    ),
    () => (
      <Modal
        id="add-staff-success"
        title={t(successTitle)}
        actions={[
          {
            label: t(
              translations.professional_caregivers.root.modals.add_staff_flow.success.confirm
            ),
            type: 'nondestructive',
            onClick: nextStep,
          },
        ]}
        {...defaultDialogProps}
      >
        <Success invitationType={formInstance.getValues('invitationType')} />
      </Modal>
    ),
  ];

  return inFlow ? DIALOGS[currentStep]() : <></>;
};

export default AddStaffFlow;
